import React, { useEffect, useState } from "react";

import { QueryClient, QueryClientProvider } from "react-query";
import { useMediaQuery } from "@material-ui/core";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import ColorModeContext from "./layout/themeContext";

import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Routes from "./routes";

const queryClient = new QueryClient();

const App = () => {
  const [locale, setLocale] = useState();

  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(
    preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light"
  );

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
      },
      setThemeMode: (newMode) => {
        setMode(newMode);
      }
    }),
    []
  );

  const theme = createTheme(
    {
      typography: {
        fontFamily: '"Inter", "Poppins", sans-serif',
        h1: { fontWeight: 700 },
        h2: { fontWeight: 700 },
        h3: { fontWeight: 600 },
        h4: { fontWeight: 600 },
        h5: { fontWeight: 600 },
        h6: { fontWeight: 600 },
        subtitle1: { fontWeight: 500 },
        subtitle2: { fontWeight: 500 },
        body1: { fontWeight: 400 },
        body2: { fontWeight: 400 },
        button: { fontWeight: 600, textTransform: "none" },
      },
      shape: {
        borderRadius: 12,
      },
      overrides: {
        MuiButton: {
          root: {
            borderRadius: 10,
            textTransform: "none",
            fontWeight: 600,
          },
        },
        MuiPaper: {
          rounded: {
            borderRadius: 12,
          },
        },
        MuiCard: {
          root: {
            borderRadius: 12,
          },
        },
        MuiDialog: {
          paper: {
            borderRadius: 16,
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          },
        },
        MuiDialogTitle: {
          root: {
            fontFamily: '"Inter", "Poppins", sans-serif',
            fontWeight: 700,
            fontSize: "1.1rem",
            padding: "20px 24px 12px",
          },
        },
        MuiDialogContent: {
          root: {
            padding: "16px 24px",
          },
          dividers: {
            borderColor: mode === "light" ? "#E2E8F0" : "rgba(255,255,255,0.08)",
          },
        },
        MuiDialogActions: {
          root: {
            padding: "12px 24px 20px",
            gap: 8,
          },
        },
        MuiOutlinedInput: {
          root: {
            borderRadius: 10,
            fontFamily: '"Inter", "Poppins", sans-serif',
            fontSize: 14,
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#CBD5E1",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#35af9a",
              borderWidth: 1.5,
            },
          },
          notchedOutline: {
            borderColor: mode === "light" ? "#E2E8F0" : "rgba(255,255,255,0.12)",
          },
        },
        MuiInputLabel: {
          root: {
            fontFamily: '"Inter", "Poppins", sans-serif',
            fontSize: 14,
          },
        },
        MuiSelect: {
          outlined: {
            borderRadius: 10,
          },
        },
        MuiMenuItem: {
          root: {
            fontFamily: '"Inter", "Poppins", sans-serif',
            fontSize: 14,
          },
        },
      },
      scrollbarStyles: {
        "&::-webkit-scrollbar": {
          width: "5px",
          height: "5px",
        },
        "&::-webkit-scrollbar-thumb": {
          boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.1)",
          backgroundColor: mode === "light" ? "#CBD5E1" : "#475569",
          borderRadius: "5px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "transparent",
        },
      },

      scrollbarStylesSoft: {
        "&::-webkit-scrollbar": {
          width: "8px",
          height: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: mode === "light" ? "#E2E8F0" : "#334155",
          borderRadius: "10px",
        },
      },
      palette: {
        type: mode,
        primary: { main: "#35af9a", contrastText: "#FFFFFF" },
        textPrimary: mode === "light" ? "#35af9a" : "#FFFFFF",
        borderPrimary: mode === "light" ? "#35af9a" : "#FFFFFF",
        dark: { main: mode === "light" ? "#1E293B" : "#F1F5F9" },
        light: { main: mode === "light" ? "#F1F5F9" : "#1E293B" },
        tabHeaderBackground: mode === "light" ? "#F1F5F9" : "#1E293B",
        optionsBackground: mode === "light" ? "#F8FAFC" : "#1E293B",
        fancyBackground: mode === "light" ? "#F1F5F9" : "#0F172A",
        total: mode === "light" ? "#fff" : "#1E293B",
        messageIcons: mode === "light" ? "#94A3B8" : "#CBD5E1",
        inputBackground: mode === "light" ? "#FFFFFF" : "#1E293B",
        barraSuperior: mode === "light" ? "#FFFFFF" : "#1E293B",
        sidebar: {
          background: mode === "light" ? "#1B2431" : "#0F172A",
          icon: mode === "light" ? "#94A3B8" : "#64748B",
          iconActive: "#FFFFFF",
          activeBackground: "#35af9a",
          hoverBackground: mode === "light" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.05)",
          text: mode === "light" ? "#94A3B8" : "#64748B",
          textActive: "#FFFFFF",
          divider: mode === "light" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.05)",
        },
        appBar: {
          background: mode === "light" ? "#FFFFFF" : "#1E293B",
          text: mode === "light" ? "#1E293B" : "#F1F5F9",
          shadow: mode === "light"
            ? "0 1px 3px 0 rgba(0,0,0,0.08), 0 1px 2px -1px rgba(0,0,0,0.06)"
            : "0 1px 3px 0 rgba(0,0,0,0.3)",
        },
      },
      mode,
    },
    locale
  );

  useEffect(() => {
    const i18nlocale = localStorage.getItem("i18nextLng");
    const browserLocale =
      i18nlocale.substring(0, 2) + i18nlocale.substring(3, 5);

    if (browserLocale === "ptBR") {
      setLocale(ptBR);
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  return (
    <ColorModeContext.Provider value={{ colorMode }}>
      <ThemeProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
          <ActiveMenuProvider>
            <Routes />
          </ActiveMenuProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default App;
